import React from 'react';
import {
  Box, Card, Chip, Stack 
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CampaignIcon from '@mui/icons-material/Campaign';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteIcon from '@mui/icons-material/Favorite';

import { formatDistanceToNow } from 'date-fns';

export const NotificationShell = styled(Card, {
  shouldForwardProp: (prop) => !['accent', 'read'].includes(prop),
})(({ theme, accent, read }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(accent || theme.palette.divider, 0.35)}`,
  background: `linear-gradient(180deg, ${alpha('#FFFFFF', 0.75)}, ${alpha('#FFFFFF', 0.55)})`,
  backdropFilter: 'blur(6px)',
  boxShadow: read
    ? `0 8px 24px ${alpha(accent || theme.palette.common.black, 0.08)}`
    : `0 10px 28px ${alpha(accent || '#000', 0.12)}`,
  transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 14px 36px ${alpha(accent || '#000', 0.16)}`,
  },
}));

export  const AccentBar = styled('span')(({ color }) => ({
  position: 'absolute',
  left: 0, top: 0, bottom: 0, width: 4,
  borderTopLeftRadius: 16, borderBottomLeftRadius: 16,
  background: color,
}));


export  const HeaderRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: 12 });
export const ActionsRow = styled(Box)(({ theme }) => ({ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: theme.spacing(1.25) }));



const routeFor = (type, obj) => {
  // Use idRaw for route generation so UUIDs retain hyphens expected by backend routes
  const id = idRaw(obj);
  switch (type) {
    case 'consortium': return id ? `/consortium/${id}` : null;
    case 'call': return id ? `/funding/call/${id}` : null;
    case 'user': return id ? `/users/${id}` : null;
    case 'org':
    case 'organization': return id ? `/consortium/orgs/${id}` : null;
    case 'proposal': return id ? `/proposals/${id}` : null;
    case 'invitation': return id ? `/invitations/${id}` : null;
    case 'suggestion': return id ? `/suggestions/${id}` : null;
    case 'project': return id ? `/projects/${id}` : null;
    case 'ticket': return id ? `/support/${id}` : null;
    default: return null;
  }
};

const pickLabel = (obj) =>
  obj?.name || obj?.title || obj?.code || obj?.username || obj?.email || 'Untitled';



export const ReferenceChips = ({ notification, onOpen }) => {
  // Build a normalized list of reference objects to render as chips.
  // Sources:
  //  - notification.references (array or JSON string)
  //  - notification.payload (commonly contains consortium_name + consortium_invitation_id)
  const payload = notification?.payload || {};

  const refs = [];

  // Helper: safely parse a JSON string into an array/object
  const parseMaybeJSON = (val) => {
    if (!val) return null;
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      const parsed = safeParseJSON(val);
      return Array.isArray(parsed) ? parsed : parsed ? [parsed] : null;
    }
    if (typeof val === 'object') return [val];
    return null;
  };

  // Helper: normalize a single reference record into our chip shape
  const pushRef = (raw) => {
    if (!raw) return;
    const type = raw.type || raw.kind;
    const obj = raw.object || raw.obj || raw.entity || raw;

    // Prefer explicit id fields, fall back to public_id or nested ids
    const id = (obj && (obj.public_id || obj.id || obj.pk || obj.uuid || obj.consortium_invitation_id)) || null;
    const label = raw.label || pickLabel(obj) || payload.consortium_name || 'Item';
    const href = routeFor(type, obj || { id });

    refs.push({ type: type || 'item', id, label, href });
  };

  // Add any structured references from notification.references (supports stringified JSON)
  const rawRefs = parseMaybeJSON(notification?.references);
  (rawRefs || []).forEach((r) => pushRef(r));

  // Also include consortium reference from payload if present and not already included
  const consortiumId = payload?.consortium_id || null;
  if (payload?.consortium_name && consortiumId) {
    const already = refs.some((r) => r.type === 'consortium' && String(r.id) === String(consortiumId));
    if (!already) {
      pushRef({ type: 'consortium', object: { id: consortiumId, name: payload.consortium_name }, label: payload.consortium_name });
    }
  }

  // Suggestion-related references: support payload and message-embedded JSON
  try {
    const parsedMsg = safeParseJSON(notification?.message);
    const suggestionId = payload.target_id || parsedMsg?.suggestion_id || (payload?.suggestion && (payload.suggestion.id || payload.suggestion.pk));
    const ntype = payload.target_type
    const suggestionTitle = payload?.target_name || parsedMsg?.suggestion_title || (payload?.suggestion && (payload.suggestion.title || payload.suggestion.name));
    if (suggestionId) {
      const alreadySug = refs.some((r) => (r.type === 'suggestion' || r.type === 'suggestion_received') && String(r.id) === String(suggestionId));
      if (!alreadySug) {
        pushRef({ type: ntype, object: { id: suggestionId, title: suggestionTitle }, label: suggestionTitle || 'Suggestion' });
      }
    }
  } catch (e) {
    // ignore parse errors — safeParseJSON already guards, but be defensive
  }

  // Call-related reference: surface call title/id if present in payload or message
  try {
    const parsedMsg2 = safeParseJSON(notification?.message);
    const callId = payload?.call_id || parsedMsg2?.call_id || (payload?.call && (payload.call.id || payload.call.pk));
    const callTitle = payload?.call_title || parsedMsg2?.call_title || (payload?.call && (payload.call.title || payload.call.name));
    if (callTitle || callId) {
      const alreadyCall = refs.some((r) => r.type === 'call' && (callId ? String(r.id) === String(callId) : String(r.label) === String(callTitle)));
      if (!alreadyCall) {
        pushRef({ type: 'call', object: { id: callId, title: callTitle }, label: callTitle || 'Call' });
      }
    }
  } catch (e) {
    // ignore
  }

  if (!refs.length) return null;

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1, mb: 0.5 }}>
      {refs.map((r, i) => (
        <Chip
          key={`${r.type}-${r.id || i}`}
          variant="outlined"
          size="small"
          label={r.label || r.name || r.title || r.code || 'Item'}
          onClick={r.href ? () => onOpen?.(r.href) : undefined}
          clickable={!!r.href}
          sx={{ borderRadius: 10, maxWidth: 260 }}
        />
      ))}
    </Stack>
  );
};


export const formatWhen = (ts) => {
  if (!ts) return '';
  try {
    const date = new Date(ts < 1e12 ? ts * 1000 : ts);
    if (Number.isNaN(date.getTime())) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return '';
  }
};


export const iconForType = (type) => {
  switch (normalizeTypeKey(type)) {
    case 'funding_deadline': return <WarningAmberIcon />;
    case 'funding_opportunity': return <CampaignIcon />;
    case 'consortium_invitation': return <GroupAddIcon />;
    case 'collaboration_request': return <PersonIcon />;
    case 'system_update': return <InfoIcon />;
    case 'suggestion_received': return <NotificationsIcon />;
    case 'like': return <FavoriteIcon />;
    default: return <NotificationsIcon />;
  }
};



// JSON helper just for optional read-only enrichment
const safeParseJSON = (val = '') => {
  const s = String(val).trim();
  if (!s.startsWith('{') || !s.endsWith('}')) return null;
  try { return JSON.parse(s); } catch { return null; }
};


const normalizeTypeKey = (t = '') => {
  const k = String(t).trim().toLowerCase();
  if (k === 'suggestion' || k === 'suggestions' || k === 'suggestion_received' || k.startsWith('suggestion')) return 'suggestion_received';
  if (k === 'like' || k === 'likes') return 'like';
  if (k === 'consortium_invitation' || (k.startsWith('consortium') && k.includes('invitation'))) return 'consortium_invitation';
  if (k === 'collab_request') return 'collaboration_request';
  if (k === 'system' || k === 'system.info' || k === 'system_update') return 'system_update';
  return k || 'default';
};



// src/components/invitations/notificationUtils.js

// --- Basic helpers -----------------------------------------------------------
// Legacy helper: compact id without hyphens (used for keys)
export const idStr = (v) => (v == null ? '' : String(v)).replace(/-/g, '');

// idRaw preserves original id formatting (keeps hyphens) — use this for building routes
export const idRaw = (v) => {
  if (v == null) return '';
  if (typeof v === 'object') {
    const cand = v.public_id ?? v.id ?? v.pk ?? v.uuid ?? v.consortium_invitation_id ?? v.target_id ?? v.call_id;
    if (cand != null) return String(cand);
    const nested = v.object || v.obj || v.entity;
    if (nested && typeof nested === 'object') {
      const nc = nested.public_id ?? nested.id ?? nested.pk ?? nested.uuid ?? nested.consortium_invitation_id ?? nested.target_id ?? nested.call_id;
      if (nc != null) return String(nc);
    }
    return '';
  }
  return String(v);
};

export const normalizeStatus = (raw) => {
  const k = (raw || '').toString().toLowerCase();
  if (!k || k === 'invited') return 'pending';
  return k;
};

export const formatLocal = (ts) => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return '';
  }
};

// Decide the viewer role from payload & notification owner
export const computeViewerRole = ({ payload = {}, notificationUserId, currentUserId }) => {
  // Prefer explicit role if backend sends it
  const explicit = (payload.role || '').toString().toLowerCase();
  if (explicit === 'recipient' || explicit === 'inviter' || explicit === 'sender') return explicit;

  const viewerId = currentUserId ?? notificationUserId;
  const fromId = payload.from_user_id ? payload.from_user_id : null;

  if (viewerId && fromId && viewerId === fromId) return 'inviter';
  return 'recipient';
};

// Build a consistent VM per notification → used by the Card
export const buildInvitationVM = (notification, { currentUserId }) => {
  const payload = notification?.payload || {};

  const vm = {
    notificationId: notification?.id,
    created: notification?.created,
    consortiumInvitationId: payload.consortium_invitation_id || payload.id,
    consortiumName: payload.consortium_name || 'Unknown Consortium',
    fromUserName: payload.from_user_name || 'Someone',
    status: normalizeStatus(payload.invitation_status || payload.action),
    role: computeViewerRole({
      payload,
      notificationUserId: notification?.user,
      currentUserId,
    }),
  };

  // derived booleans
  vm.isRecipient = vm.role === 'recipient';
  vm.isInviter = vm.role === 'inviter' || vm.role === 'sender';
  vm.recipientPending = vm.isRecipient && vm.status === 'pending';
  vm.recipientAccepted = vm.isRecipient && vm.status === 'accepted';
  vm.inviterPending = vm.isInviter && vm.status === 'pending';
  vm.inviterAccepted = vm.isInviter && vm.status === 'accepted';

  return vm;
};



export const ActionsRowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  alignItems: 'center',
  // marginTop: theme.spacing(1.25),
}));



// Helpers (can live in notificationUtils.jsx if you prefer)
export const cap = (s='') => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
export const norm = (s='') => String(s).trim().toLowerCase();

export const buildInvitationTitle = ({
  role, status, fromUserName, toUserName, consortiumName
}) => {
  const r = norm(role);
  const st = norm(status) || 'pending';

  if (r === 'recipient') {
    if (st === 'pending' || st === 'invited') return `${fromUserName} invited you to join ${consortiumName}`;
    if (st === 'accepted')                   return `You accepted ${fromUserName}'s invitation to join ${consortiumName}`;
    if (st === 'rejected')                   return `You declined ${fromUserName}'s invitation to join ${consortiumName}`;
    if (st === 'cancelled')                  return `You cancelled your acceptance for ${consortiumName}`;
    return `Invitation to join ${consortiumName}`;
  }

  // inviter / sender
  if (st === 'pending' || st === 'invited')  return `Invitation sent to ${toUserName} for ${consortiumName}`;
  if (st === 'accepted')                     return `${toUserName} accepted your invitation to join ${consortiumName}`;
  if (st === 'rejected')                     return `${toUserName} rejected your invitation to join ${consortiumName}`;
  if (st === 'cancelled')                    return `${toUserName} cancelled their acceptance for ${consortiumName}`;
  return `Invitation update for ${consortiumName}`;
};



 